// Is this page running the version the server serves? (Claude, 2026-10-08)
// Fernando, 8.10: a tab opened before an update kept the old code for hours — Henrik never saw the new Move button.
// Every 2 minutes, and when the tab comes back into view, /index.html (~1 KB, never cached) is read and the bundle
// it names (static/js/main.<hash>.js) is compared with the one this page runs. When the server has a newer one the
// page is "stale": App shows a banner and the next page change is a full load; the inventory reloads itself at a
// safe moment (never during a count). Locally (npm start) there is no hash and nothing is checked.
import { useEffect, useState } from 'react';

const MAIN_RE = /static\/js\/main\.([0-9a-f]+)\.js/;

function readOwnBuild(): string {
  try {
    const el = document.querySelector('script[src*="/static/js/main."]') as HTMLScriptElement | null;
    const m = el?.src.match(MAIN_RE);
    return m ? m[1] : 'dev';
  } catch { return 'dev'; }
}

/** The bundle hash this page runs ("dev" locally) — sent as X-WMS-Build with every request (api.ts). */
export const OWN_BUILD = readOwnBuild();

let stale = false;
let started = false;
let checking: Promise<boolean> | null = null;
const subscribers = new Set<(s: boolean) => void>();

/** Reads /index.html once; true when the server serves a different bundle. A missing or unreadable page (a build
 *  in progress empties the folder for a few minutes) never counts as stale. */
export function checkBuild(): Promise<boolean> {
  if (OWN_BUILD === 'dev' || stale) return Promise.resolve(stale);
  if (checking) return checking;
  checking = (async () => {
    try {
      const r = await fetch('/index.html', { cache: 'no-store' });
      if (r.ok) {
        const m = (await r.text()).match(MAIN_RE);
        if (m && m[1] !== OWN_BUILD) {
          stale = true;
          subscribers.forEach(f => f(true));
        }
      }
    } catch { /* offline — the next check tries again */ }
    finally { checking = null; }
    return stale;
  })();
  return checking;
}

export function startBuildCheck(): void {
  if (started || OWN_BUILD === 'dev') return;
  started = true;
  window.setInterval(() => { checkBuild(); }, 120_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') checkBuild(); });
  window.addEventListener('focus', () => { checkBuild(); });
}

export function isStale(): boolean { return stale; }

export function useBuildStale(): boolean {
  const [s, setS] = useState(stale);
  useEffect(() => {
    const f = (v: boolean) => setS(v);
    subscribers.add(f);
    setS(stale);
    return () => { subscribers.delete(f); };
  }, []);
  return s;
}

/** A full load of the current address: the new code, nothing of the old page kept. Returns false (and does nothing)
 *  when this same old version already reloaded itself less than a minute ago — a page that still comes back old
 *  must never loop; the banner stays and the person can press Reload. `force` (the banner's button) always reloads. */
export function reloadFresh(force = false): boolean {
  if (!force) {
    try {
      const last = JSON.parse(sessionStorage.getItem('wms_reload_guard') || 'null');
      if (last && last.from === OWN_BUILD && Date.now() - last.t < 60_000) return false;
      sessionStorage.setItem('wms_reload_guard', JSON.stringify({ from: OWN_BUILD, t: Date.now() }));
    } catch { /* no storage: reload anyway */ }
  }
  window.location.reload();
  return true;
}
